import React, { useState, useEffect } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { Organization } from '../../types/organization';
import { organizationService } from '../../services/organizationService';
import { PlusIcon, PencilSquareIcon, TrashIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import LoadingScreen from '../common/LoadingScreen';

interface OrganizationFormData {
  name: string;
}

interface OrganizationListProps {
  selectedOrg: Organization | null;
  onSelectOrg: (org: Organization) => void;
  onEdit: () => void;
}

const OrganizationList: React.FC<OrganizationListProps> = ({
  selectedOrg,
  onSelectOrg,
  onEdit
}) => {
  const { organizations, loading, refetch } = useOrganizations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<OrganizationFormData>({ name: '' });
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<string | null>(null);
  const { user } = useAuth();
  const { t } = useLanguage();

  // Modal megnyitása új szervezet létrehozásához
  const handleOpenCreateModal = () => {
    setEditingOrg(null);
    setFormData({ name: '' });
    setIsModalOpen(true);
  };

  // Modal megnyitása szerkesztéshez
  const handleOpenEditModal = (org: Organization) => {
    setEditingOrg(org);
    setFormData({ name: org.name });
    setIsModalOpen(true);
  };

  // Modal bezárása
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOrg(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        const updatedOrg = {
          ...editingOrg,
          name: formData.name,
          updatedAt: new Date()
        };

        await organizationService.updateOrganization(editingOrg.id, updatedOrg);
        onSelectOrg(updatedOrg);
        toast.success(t('common.messages.organization.updateSuccess'));
      } else {
        const newOrg = {
          name: formData.name,
          ownerId: user?.id || '',
          taxNumber: '',
          registrationNumber: '',
          address: {
            street: '',
            city: '',
            postalCode: '',
            country: ''
          },
          contact: {
            name: '',
            email: '',
            phone: '',
            position: ''
          },
          users: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const createdOrg = await organizationService.createOrganization(newOrg);
        if (createdOrg) {
          onSelectOrg(createdOrg);
        }
        toast.success(t('common.messages.organization.createdSuccess'));
      }

      // Először frissítjük az adatokat
      await refetch();
      // Aztán zárjuk be a modalt
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting organization:', error);
      toast.error(t('common.messages.organization.createdError'));
    }
  };

  const handleDelete = async (orgId: string) => {
    setOrgToDelete(orgId);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (orgToDelete) {
      try {
        await organizationService.deleteOrganization(orgToDelete);
        await refetch(); // Megvárjuk a frissítést
        toast.success(t('common.messages.organization.deleteSuccess'));
      } catch (error) {
        console.error('Error deleting organization:', error);
        toast.error(t('common.messages.organization.deleteError'));
      }
      setIsConfirmDeleteOpen(false);
      setOrgToDelete(null);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 mb-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0">
        <h2 className="text-xl font-semibold text-gray-800">{t('organizations.title')}</h2>
        <button
          onClick={handleOpenCreateModal}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('organizations.buttons.new')}
        </button>
      </div>

      <div className="grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {organizations.map((org) => (
          <div
            key={org.id}
            className={`p-4 rounded-lg border transition-all cursor-pointer
              ${selectedOrg?.id === org.id
                ? 'bg-white border-primary-500/70 shadow-md'
                : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`}
            onClick={() => onSelectOrg(org)}
          >
            <div className="flex flex-col space-y-3">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500/10 to-primary-600/10 border border-primary-500/10">
                  <BuildingOffice2Icon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectOrg(org);
                      onEdit();
                    }}
                    className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(org.id);
                    }}
                    className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 truncate">{org.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`
                    inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                    ${org.ownerId === user?.id
                      ? 'bg-primary-500/20 text-primary-700'
                      : org.users?.find(u => u.userId === user?.id)?.role === 'admin'
                        ? 'bg-purple-500/20 text-purple-700'
                        : 'bg-gray-200 text-gray-700'}
                  `}>
                    {org.ownerId === user?.id
                      ? t('organizations.roles.owner')
                      : org.users?.find(u => u.userId === user?.id)?.role === 'admin'
                        ? t('organizations.roles.administrator')
                        : t('organizations.roles.member')}
                  </span>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200 space-y-2">
                {org.taxNumber && (
                  <div className="flex items-center text-xs text-gray-600">
                    <span className="font-medium mr-2 text-gray-700">{t('organizations.fields.company.taxNumber')}:</span>
                    <span>{org.taxNumber}</span>
                  </div>
                )}

                {org.registrationNumber && (
                  <div className="flex items-center text-xs text-gray-600">
                    <span className="font-medium mr-2 text-gray-700">{t('organizations.fields.company.registrationNumber')}:</span>
                    <span>{org.registrationNumber}</span>
                  </div>
                )}

                {org.address?.city && (
                  <div className="flex items-center text-xs text-gray-600">
                    <span className="font-medium mr-2 text-gray-700">{t('organizations.fields.address.location')}:</span>
                    <span className="truncate">
                      {org.address.city}, {org.address.country}
                    </span>
                  </div>
                )}

                <div className="flex items-center text-xs text-gray-600">
                  <span className="font-medium mr-2 text-gray-700">{t('organizations.roles.members')}:</span>
                  <span>{(org.users?.length || 0) + 1}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for adding/editing organizations */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-xl font-medium text-gray-800 mb-4">
              {editingOrg ? t('organizations.buttons.edit') : t('organizations.buttons.new')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('organizations.fields.name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-700 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  {t('organizations.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  {editingOrg ? t('organizations.buttons.saveChanges') : t('organizations.buttons.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setOrgToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('organizations.messages.confirmTitle') || "Szervezet törlése"}
        deleteMessage={t('organizations.messages.deleteQuestion') || "Biztosan törölni szeretné ezt a szervezetet?"}
        deleteWarning={t('organizations.messages.deleteWarning') || "Ez a művelet nem visszavonható."}
      />
    </div>
  );
};

export default OrganizationList; 